import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar";
import API from "../../api/api";
import SearchableSelect from "../../components/common/SearchableSelect";
import { toastMessageAlert } from "../../utils/alert";

const normalizeType = (value) =>
  String(value || "")
    .toUpperCase()
    .replace(/\s*\/\s*/g, "_")
    .replace(/\s+/g, "_")
    .trim();

const isTimedType = (type) => {
  const t = normalizeType(type);
  if (!t) return false;
  if (t === "TIMED" || t === "TRACK") return true;
  return t.includes("TIMED") || t.includes("RACE") || t.includes("TRACK");
};
const isFieldType = (type) => ["POINTS", "DISTANCE", "DISTANCE_HEIGHT", "MAX_WEIGHT"].includes(normalizeType(type));
const isTeamOrRoundType = (type) => {
  const t = normalizeType(type);
  return ["BRACKET", "KNOCKOUT", "LEAGUE", "TEAM", "TEAM_SPORT", "MATCH", "ROUND"].includes(t);
};
const isLikelyTimed = ({ type, gameName, narration, sportName }) => {
  if (isTimedType(type)) return true;
  const g = String(gameName || "").toLowerCase();
  const n = String(narration || "").toLowerCase();
  const s = String(sportName || "").toLowerCase();
  if (s.includes("swim")) return true;
  if (/(track|timed|race|run)/.test(`${g} ${n}`)) return true;
  return /(freestyle|backstroke|breaststroke|butterfly|medley|relay|sprint|run|marathon|time trial|pursuit|race|\\b\\d+m\\b)/.test(
    `${g} ${n}`
  );
};
const inferEntryMode = ({ type, gameName, narration, sportName }) => {
  if (isLikelyTimed({ type, gameName, narration, sportName })) return "time";
  if (isFieldType(type)) return "distance";
  if (isTeamOrRoundType(type) || /(round|set|match|knockout|league|team|bracket)/.test(String(type || "").toLowerCase())) {
    return "round_score";
  }
  return "points";
};

const rowKey = (r) => `${r.registration_id}_${r.game_id || 0}`;
const formatTimeMask = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, 7);
  if (!digits) return "";
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  return `${digits.slice(0, 2)}:${digits.slice(2, 4)}:${digits.slice(4, 7)}`;
};

const normalizeTimeText = (raw) => {
  const txt = String(raw || "").replace(/[^\d:.\s]/g, "").replace(/\s+/g, "");
  if (!txt) return "";
  return formatTimeMask(txt);
};
const isFullMaskedTime = (value) => /^\d{2}:\d{2}:\d{1,3}$/.test(String(value || "").trim());
const msToMaskedTime = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const ms = Number(value);
  if (!Number.isFinite(ms) || ms < 0) return "";
  const mm = Math.floor(ms / 60000);
  const ss = Math.floor((ms % 60000) / 1000);
  const mmm = Math.floor(ms % 1000);
  return `${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}:${String(mmm).padStart(3, "0")}`;
};
const toEntryValue = (v) => (v === null || v === undefined ? "" : v);
const hasEntryValue = (v) => !(v === null || v === undefined || v === "");

export default function ResultsManager() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateFromQuery = searchParams.get("date") || "";
  const eventIdFromQuery = searchParams.get("event_id") || "";
  const gameIdFromQuery = searchParams.get("game_id") || "";

  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [classificationCounts, setClassificationCounts] = useState([]);

  const [eventId, setEventId] = useState(eventIdFromQuery);
  const [gameId, setGameId] = useState(gameIdFromQuery);
  const [gender, setGender] = useState((searchParams.get("gender") || "all").toLowerCase());
  const [selectedClassificationIds, setSelectedClassificationIds] = useState([]);
  const [mixClassificationsForCompute, setMixClassificationsForCompute] = useState(false);
  const [entryMode, setEntryMode] = useState("auto");

  const [players, setPlayers] = useState([]);
  const [entryByRow, setEntryByRow] = useState({});

  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [liveSettingsVisible, setLiveSettingsVisible] = useState(false);
  const [liveSettingsLoading, setLiveSettingsLoading] = useState(false);
  const [liveSettingsSaving, setLiveSettingsSaving] = useState(false);
  const [liveEnabled, setLiveEnabled] = useState(true);
  const [pauseMessage, setPauseMessage] = useState("");

  useEffect(() => {
    if (!message) return;
    toastMessageAlert(message, { timer: 2400 });
  }, [message]);

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(eventId)) || null,
    [events, eventId]
  );
  const selectedGame = useMemo(
    () => games.find((g) => String(g.id) === String(gameId)) || null,
    [games, gameId]
  );
  const gameType = useMemo(() => normalizeType(selectedGame?.game_type || players[0]?.game_type || ""), [selectedGame, players]);
  const inferredEntryMode = useMemo(
    () =>
      inferEntryMode({
        type: gameType,
        gameName: selectedGame?.game_name || players[0]?.game_name,
        narration: selectedGame?.game_narration || players[0]?.game_narration,
        sportName: selectedEvent?.sport_name || "",
      }),
    [gameType, selectedGame, players, selectedEvent]
  );
  const effectiveEntryMode = entryMode === "auto" ? inferredEntryMode : entryMode;
  const unitHint =
    effectiveEntryMode === "time"
      ? "Format: mm:ss:ms (1-3 digits)"
      : effectiveEntryMode === "distance"
      ? "Unit: metres (m)"
      : effectiveEntryMode === "round_score"
      ? "Unit: round score"
      : "Unit: points (pts)";

  const activeGenderLabel = gender === "male" ? "Male" : gender === "female" ? "Female" : "All";
  const gameDayContextQuery = useMemo(() => {
    const q = new URLSearchParams();
    if (dateFromQuery) q.set("date", dateFromQuery);
    if (eventId) q.set("event_id", String(eventId));
    q.set("gender", gender || "all");
    return q.toString();
  }, [dateFromQuery, eventId, gender]);

  useEffect(() => {
    const loadEvents = async () => {
      setLoading(true);
      try {
        const res = await API.fetchAdminEvents();
        const list = res?.data || res || [];
        setEvents(Array.isArray(list) ? list : []);
      } catch (err) {
        setEvents([]);
        setMessage(err?.message || "Failed to load events.");
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  useEffect(() => {
    const loadLiveSettings = async () => {
      setLiveSettingsLoading(true);
      try {
        const res = await API.fetchLiveResultSettings();
        setLiveEnabled(res?.is_enabled !== false);
        setPauseMessage(String(res?.pause_message || ""));
        setLiveSettingsVisible(true);
      } catch (_err) {
        setLiveSettingsVisible(false);
      } finally {
        setLiveSettingsLoading(false);
      }
    };

    loadLiveSettings();
  }, []);

  useEffect(() => {
    setEntryMode("auto");
  }, [gameId]);

  useEffect(() => {
    const loadContext = async () => {
      if (!eventId) {
        setGames([]);
        setClassifications([]);
        setClassificationCounts([]);
        setGameId("");
        setSelectedClassificationIds([]);
        setPlayers([]);
        setEntryByRow({});
        return;
      }

      try {
        const [ctxRes, clsRes] = await Promise.all([
          API.fetchResultEventContext(eventId),
          selectedEvent?.sport_id
            ? API.get(`/admin/classifications/sports/${selectedEvent.sport_id}/classifications`)
            : Promise.resolve([]),
        ]);

        const ctx = ctxRes?.data || ctxRes || {};
        const gameList = Array.isArray(ctx?.games) ? ctx.games : [];
        const clsList = clsRes?.data || clsRes || [];

        setGames(gameList);
        setClassifications(Array.isArray(clsList) ? clsList : []);
        setGameId((prev) => {
          const prevValue = String(prev || "");
          if (prevValue && gameList.some((g) => String(g.id) === prevValue)) {
            return prevValue;
          }
          if (gameIdFromQuery && gameList.some((g) => String(g.id) === String(gameIdFromQuery))) {
            return String(gameIdFromQuery);
          }
          return "";
        });
        setSelectedClassificationIds([]);
        setPlayers([]);
        setEntryByRow({});
      } catch (err) {
        setGames([]);
        setClassifications([]);
        setClassificationCounts([]);
        setMessage(err?.message || "Failed to load game/classification context.");
      }
    };

    loadContext();
  }, [eventId, selectedEvent, gameIdFromQuery]);

  useEffect(() => {
    const loadClassificationCounts = async () => {
      if (!eventId || !gameId) {
        setClassificationCounts([]);
        return;
      }

      try {
        const res = await API.get(`/admin/results/event/${eventId}/classifications-summary`, {
          params: {
            game_id: gameId,
            gender: gender === "all" ? undefined : gender,
          },
        });
        const rows = res?.data || res || [];
        setClassificationCounts(Array.isArray(rows) ? rows : []);
      } catch (_err) {
        setClassificationCounts([]);
      }
    };

    loadClassificationCounts();
  }, [eventId, gameId, gender]);

  const hydrateEntriesFromPlayers = (list, prevEntries = {}, options = {}) => {
    const { preserveManual = false, preferServerComputed = false } = options;
    const next = {};
    list.forEach((r) => {
      const key = rowKey(r);
      const prev = prevEntries[key] || {};
      let loadedTime = hasEntryValue(r.result_time) ? String(r.result_time) : msToMaskedTime(r.result_time_ms);
      const rawMs = Number(r.result_time_ms);
      if (loadedTime === "00:00:000" && (!hasEntryValue(r.result_time_ms) || !Number.isFinite(rawMs) || rawMs <= 0)) {
        loadedTime = "";
      }
      next[key] = {
        result_time: preserveManual
          ? toEntryValue(hasEntryValue(prev.result_time) ? prev.result_time : loadedTime)
          : toEntryValue(loadedTime),
        result_position: preferServerComputed
          ? toEntryValue(hasEntryValue(r.result_position) ? r.result_position : prev.result_position)
          : preserveManual
          ? toEntryValue(hasEntryValue(prev.result_position) ? prev.result_position : r.result_position)
          : toEntryValue(r.result_position),
        points_score: preferServerComputed
          ? toEntryValue(hasEntryValue(r.points_score) ? r.points_score : prev.points_score)
          : preserveManual
          ? toEntryValue(hasEntryValue(prev.points_score) ? prev.points_score : r.points_score)
          : toEntryValue(r.points_score),
        attempts: Array.isArray(prev.attempts) ? prev.attempts : ["", "", ""],
      };
    });
    return next;
  };

  const loadPlayersManual = async (options = {}) => {
    const { preserveManual = false, preferServerComputed = false, silentMessage = false } = options;
    if (!eventId || !gameId) {
      setMessage("Select event and game.");
      return [];
    }

    setBusy(true);
    setMessage("");
    try {
      const params = {
        game_id: Number(gameId),
        gender: gender === "all" ? undefined : gender,
        classification_ids: selectedClassificationIds.length ? selectedClassificationIds : undefined,
      };
      const res = await API.fetchEventPlayers(eventId, params);
      const rows = res?.data || res || [];
      const list = Array.isArray(rows) ? rows : [];
      setPlayers(list);
      setEntryByRow((prev) =>
        hydrateEntriesFromPlayers(list, prev, { preserveManual, preferServerComputed })
      );
      if (!silentMessage) {
        setMessage(`Loaded ${list.length} players for manual result entry.`);
      }
      return list;
    } catch (err) {
      setPlayers([]);
      setEntryByRow({});
      setMessage(err?.message || "Failed to load players.");
      return [];
    } finally {
      setBusy(false);
    }
  };

  const setEntryField = (r, field, value) => {
    const key = rowKey(r);
    setEntryByRow((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || { attempts: ["", "", ""] }),
        [field]: value,
      },
    }));
  };

  const setAttempt = (r, index, value) => {
    const key = rowKey(r);
    setEntryByRow((prev) => {
      const curr = prev[key] || { result_time: "", result_position: "", points_score: "", attempts: ["", "", ""] };
      const attempts = Array.isArray(curr.attempts) ? [...curr.attempts] : ["", "", ""];
      attempts[index] = value;
      return {
        ...prev,
        [key]: {
          ...curr,
          attempts,
        },
      };
    });
  };

  const persistManualResults = async () => {
    if (!players.length) {
      setMessage("Load players first.");
      return false;
    }

    if (effectiveEntryMode === "time") {
      const invalidRows = [];
      const results = players
      .map((r) => {
        const v = entryByRow[rowKey(r)] || {};
        const normalizedTime = normalizeTimeText(v.result_time || "").trim();
        if (normalizedTime !== "" && !isFullMaskedTime(normalizedTime)) {
          invalidRows.push(r.name || `Reg#${r.registration_id}`);
          return null;
        }
        if (normalizedTime === "" && (v.result_position === "" || v.result_position === null || v.result_position === undefined)) {
          return null;
        }
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          result_time: normalizedTime || null,
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      })
      .filter(Boolean);

      if (invalidRows.length) {
        throw new Error(`Invalid time format for: ${invalidRows.slice(0, 5).join(", ")}. Use mm:ss:ms (1-3 digits).`);
      }
      if (!results.length) {
        throw new Error("Enter at least one valid time in mm:ss:ms (1-3 digits) before saving/computing.");
      }
      await API.saveTimedResults({ results });
    } else if (effectiveEntryMode === "distance") {
      const attemptsPayload = [];
      players.forEach((r) => {
        const v = entryByRow[rowKey(r)] || {};
        const attempts = Array.isArray(v.attempts) ? v.attempts : [];
        attempts.forEach((score, idx) => {
          const normalizedScore = String(score ?? "").trim();
          if (normalizedScore !== "") {
            attemptsPayload.push({
              event_reg_id: Number(r.registration_id),
              registration_id: Number(r.registration_id),
              game_id: Number(r.game_id || gameId),
              selected_game_id: Number(gameId || 0) || null,
              attempt_no: idx + 1,
              score: normalizedScore,
            });
          }
        });
      });
      if (attemptsPayload.length) {
        await API.savePointsAttempts({ results: attemptsPayload });
      }

      const manualPositions = players
        .map((r) => {
          const v = entryByRow[rowKey(r)] || {};
          if (v.result_position === "" || v.result_position === null || v.result_position === undefined) {
            return null;
          }
          return {
            registration_id: Number(r.registration_id),
            game_id: Number(r.game_id || gameId),
            result_position: Number(v.result_position),
          };
        })
        .filter(Boolean);

      if (manualPositions.length) {
        await API.post("/admin/results/generic/save", { results: manualPositions });
      }
    } else if (effectiveEntryMode === "round_score" || effectiveEntryMode === "points") {
      const results = players.map((r) => {
        const v = entryByRow[rowKey(r)] || {};
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          points_score: v.points_score !== "" ? Number(v.points_score) : null,
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      });
      await API.post("/admin/results/generic/save", { results });
    } else {
      const results = players.map((r) => {
        const v = entryByRow[rowKey(r)] || {};
        return {
          registration_id: Number(r.registration_id),
          game_id: Number(r.game_id || gameId),
          result_position: v.result_position !== "" ? Number(v.result_position) : null,
        };
      });
      await API.post("/admin/results/generic/save", { results });
    }
    return true;
  };

  const saveManualResults = async () => {
    setBusy(true);
    setMessage("");
    try {
      const ok = await persistManualResults();
      if (ok) setMessage("Manual results saved.");
    } catch (err) {
      setMessage(err?.message || "Failed to save results.");
    } finally {
      setBusy(false);
    }
  };

  const computeResults = async () => {
    if (!eventId) {
      setMessage("Select event first.");
      return;
    }
    if (!gameId) {
      setMessage("Select game first.");
      return;
    }
    if (mixClassificationsForCompute && selectedClassificationIds.length < 2) {
      setMessage("Select at least 2 classifications to mix.");
      return;
    }

    setBusy(true);
    setMessage("");
    try {
      const saved = await persistManualResults();
      if (!saved) return;
      await API.computePositions({
        event_id: Number(eventId),
        game_id: Number(gameId),
        mix_classifications: mixClassificationsForCompute,
        mix_classification_ids: mixClassificationsForCompute
          ? selectedClassificationIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
          : [],
      });
      const refreshed = await loadPlayersManual({
        preserveManual: true,
        preferServerComputed: true,
        silentMessage: true,
      });
      const hasComputedPosition = (Array.isArray(refreshed) ? refreshed : []).some((r) =>
        hasEntryValue(r?.result_position)
      );
      if (hasComputedPosition) {
        setMessage("Positions computed successfully and shown. Manual entries preserved.");
      } else {
        setMessage("Compute completed, but no positions returned. Save valid results first, then compute.");
      }
    } catch (err) {
      setMessage(err?.message || "Compute failed.");
    } finally {
      setBusy(false);
    }
  };

  const countMap = useMemo(() => {
    const map = {};
    classificationCounts.forEach((c) => {
      map[String(c.classification_id)] = Number(c.players_count || 0);
    });
    return map;
  }, [classificationCounts]);

  const saveLiveSettings = async (nextEnabled) => {
    if (!liveSettingsVisible || liveSettingsSaving) return;
    setLiveSettingsSaving(true);
    try {
      const res = await API.updateLiveResultSettings({
        is_enabled: nextEnabled,
        pause_message: pauseMessage,
      });
      setLiveEnabled(res?.data?.is_enabled !== false);
      setPauseMessage(String(res?.data?.pause_message || pauseMessage || ""));
      setMessage(res?.message || (nextEnabled ? "Live results enabled." : "Live results paused."));
    } catch (err) {
      setMessage(err?.message || "Failed to update live results setting.");
    } finally {
      setLiveSettingsSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen">
        <div className="max-w-[1450px] mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
          <section className="mb-3 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-3 sm:p-3.5 shadow-sm">
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Results Desk</p>
                <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-0.5">Results Management</h1>
              </div>
              <button
                onClick={() => navigate(`/admin/game-day?${gameDayContextQuery}`)}
                className="text-xs sm:text-sm px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              >
                Open Game Day
              </button>
            </div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Event</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{selectedEvent?.title || "None"}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Game</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{selectedGame?.game_name || "None"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Entry Mode</p>
                <p className="text-sm font-semibold text-cyan-800 capitalize">{effectiveEntryMode.replace("_", " ")}</p>
              </div>
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-amber-700">Loaded Rows</p>
                <p className="text-sm font-semibold text-amber-800">{players.length}</p>
              </div>
            </div>
          </section>

          <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)]">
            <aside className="self-start rounded-3xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-24">
              <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-600">Result Context</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Event</label>
                  <SearchableSelect
                    value={eventId}
                    onChange={(e) => setEventId(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                    disabled={loading}
                  >
                    <option value="">-- Select Event --</option>
                    {events.map((event) => (
                      <option key={event.id} value={event.id}>{event.title}</option>
                    ))}
                  </SearchableSelect>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Game</label>
                  <SearchableSelect
                    value={gameId}
                    onChange={(e) => setGameId(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                    disabled={!eventId}
                  >
                    <option value="">-- Select Game --</option>
                    {games.map((game) => (
                      <option key={game.id} value={game.id}>{game.game_name}</option>
                    ))}
                  </SearchableSelect>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Gender</label>
                  <SearchableSelect
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                  >
                    <option value="all">All</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </SearchableSelect>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600 mb-1">Entry Option</label>
                  <SearchableSelect
                    value={entryMode}
                    onChange={(e) => setEntryMode(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl p-2.5"
                  >
                    <option value="auto">Auto (from game type)</option>
                    <option value="time">Time</option>
                    <option value="distance">Distance</option>
                    <option value="round_score">Round Score</option>
                    <option value="points">Points</option>
                  </SearchableSelect>
                </div>

                <button
                  onClick={loadPlayersManual}
                  disabled={busy || !eventId || !gameId}
                  className="w-full h-11 rounded-xl bg-slate-700 text-white font-semibold hover:bg-slate-800 disabled:bg-gray-400"
                >
                  Load Players
                </button>
                <button
                  onClick={saveManualResults}
                  disabled={busy || !players.length}
                  className="w-full h-11 rounded-xl bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-400"
                >
                  Save Results
                </button>
                <button
                  onClick={computeResults}
                  disabled={busy || !eventId || !gameId}
                  className="w-full h-11 rounded-xl bg-violet-600 text-white font-semibold hover:bg-violet-700 disabled:bg-gray-400"
                >
                  Compute Positions
                </button>
              </div>

              <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Active Filter</p>
                <p className="text-sm font-semibold text-slate-900 mt-1">Gender: {activeGenderLabel}</p>
                <p className="text-xs text-slate-600 mt-1">Game Type: {gameType || "Unknown"}</p>
              </div>
            </aside>

            <div className="space-y-4">
              {liveSettingsVisible ? (
                <section className="rounded-3xl border border-blue-100 bg-white p-4 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Live Results Visibility</h2>
                      <p className="text-sm text-gray-600">
                        Control whether public users can see live results for current events.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={liveSettingsLoading || liveSettingsSaving || liveEnabled}
                        onClick={() => saveLiveSettings(true)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-300"
                      >
                        Show Live Results
                      </button>
                      <button
                        type="button"
                        disabled={liveSettingsLoading || liveSettingsSaving || !liveEnabled}
                        onClick={() => saveLiveSettings(false)}
                        className="px-3 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-300"
                      >
                        Pause Live Results
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid md:grid-cols-[1fr_auto] gap-2 items-center">
                    <input
                      type="text"
                      value={pauseMessage}
                      onChange={(e) => setPauseMessage(e.target.value)}
                      maxLength={255}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm"
                      placeholder="Pause message shown to public users (optional)"
                    />
                    <button
                      type="button"
                      disabled={liveSettingsLoading || liveSettingsSaving}
                      onClick={() => saveLiveSettings(liveEnabled)}
                      className="px-3 py-2 rounded-lg text-sm font-semibold bg-slate-700 text-white hover:bg-slate-800 disabled:bg-gray-300"
                    >
                      Save Message
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    Current status:{" "}
                    <span className={liveEnabled ? "text-green-700 font-semibold" : "text-amber-700 font-semibold"}>
                      {liveEnabled ? "Live (visible to public)" : "Paused (hidden from public)"}
                    </span>
                  </p>
                </section>
              ) : null}

              <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Classifications (checkbox) with player counts for selected game and gender
                </p>
                <div className="mb-2 flex items-center gap-2 text-sm">
                  <input
                    id="mix-categories-compute"
                    type="checkbox"
                    checked={mixClassificationsForCompute}
                    onChange={(e) => setMixClassificationsForCompute(e.target.checked)}
                  />
                  <label htmlFor="mix-categories-compute" className="text-gray-800">
                    Mix selected classifications for compute
                  </label>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm">
                  {classifications.map((c) => {
                    const id = String(c.id);
                    const checked = selectedClassificationIds.includes(id);
                    const count = Number(countMap[id] || 0);
                    return (
                      <label key={id} className="flex items-center gap-2 border rounded-xl px-2 py-1.5 bg-white">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setSelectedClassificationIds((prev) =>
                              e.target.checked ? [...prev, id] : prev.filter((x) => x !== id)
                            );
                          }}
                        />
                        <span>{c.classification} ({count})</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Result entry mode:{" "}
                  <span className="font-semibold">
                    {effectiveEntryMode === "time"
                      ? "Time"
                      : effectiveEntryMode === "distance"
                      ? "Distance"
                      : effectiveEntryMode === "round_score"
                      ? "Round Score"
                      : "Points"}
                  </span>
                  {" "}
                  <span className="text-gray-500">(Game Type: {gameType || "Unknown"})</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">{unitHint}</p>
                {selectedEvent?.sport_name?.toLowerCase().includes("athlet") || selectedEvent?.sport_id === 2 ? (
                  <p className="text-xs text-indigo-700 mt-1">
                    Athletics mode applies track and field formula points. Enable mix to combine selected classifications.
                  </p>
                ) : null}
              </section>

              {message ? <div className="text-sm bg-slate-100 p-3 rounded-xl border border-slate-200">{message}</div> : null}

              {players.length ? (
                <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="overflow-x-auto rounded-2xl border border-slate-200">
                    <table className="min-w-full border-0 text-sm">
                      <thead className="bg-blue-100">
                        <tr>
                          <th className="p-2 border">Player</th>
                          <th className="p-2 border">District</th>
                          <th className="p-2 border">Gender</th>
                          <th className="p-2 border">Category</th>
                          <th className="p-2 border">Classification</th>
                          <th className="p-2 border">Game</th>
                          {effectiveEntryMode === "time" ? <th className="p-2 border">Time</th> : null}
                          {effectiveEntryMode === "distance" ? (
                            <>
                              <th className="p-2 border">Distance 1</th>
                              <th className="p-2 border">Distance 2</th>
                              <th className="p-2 border">Distance 3</th>
                            </>
                          ) : null}
                          {effectiveEntryMode === "round_score" ? <th className="p-2 border">Round Score</th> : null}
                          {effectiveEntryMode === "points" ? <th className="p-2 border">Points</th> : null}
                          <th className="p-2 border">Position</th>
                          <th className="p-2 border">Points</th>
                        </tr>
                      </thead>
                      <tbody>
                        {players.map((p) => {
                          const key = rowKey(p);
                          const v = entryByRow[key] || { attempts: ["", "", ""] };
                          return (
                            <tr key={key}>
                              <td className="p-2 border">{p.name}</td>
                              <td className="p-2 border">{p.district || "-"}</td>
                              <td className="p-2 border">{p.gender || "-"}</td>
                              <td className="p-2 border">{p.age_category || "-"}</td>
                              <td className="p-2 border">{p.classification_name || "Unclassified"}</td>
                              <td className="p-2 border">{p.game_name || "-"}</td>

                              {effectiveEntryMode === "time" ? (
                                <td className="p-2 border">
                                  <input
                                    type="text"
                                    value={v.result_time || ""}
                                    onChange={(e) => {
                                      const next = formatTimeMask(e.target.value);
                                      setEntryField(p, "result_time", next);
                                    }}
                                    placeholder="mm:ss:ms"
                                    className="border p-1 w-28"
                                  />
                                </td>
                              ) : null}

                              {effectiveEntryMode === "distance" ? (
                                <>
                                  {[0, 1, 2].map((i) => (
                                    <td key={i} className="p-2 border">
                                      <input
                                        type="number"
                                        step="any"
                                        value={(v.attempts || ["", "", ""])[i] || ""}
                                        onChange={(e) => setAttempt(p, i, e.target.value)}
                                        className="border p-1 w-24"
                                      />
                                    </td>
                                  ))}
                                </>
                              ) : null}

                              {effectiveEntryMode === "round_score" || effectiveEntryMode === "points" ? (
                                <td className="p-2 border">
                                  <input
                                    type="number"
                                    step="any"
                                    value={v.points_score || ""}
                                    onChange={(e) => setEntryField(p, "points_score", e.target.value)}
                                    className="border p-1 w-24"
                                  />
                                </td>
                              ) : null}

                              <td className="p-2 border">
                                <input
                                  type="number"
                                  min="1"
                                  value={
                                    hasEntryValue(v.result_position)
                                      ? v.result_position
                                      : hasEntryValue(p.result_position)
                                      ? p.result_position
                                      : ""
                                  }
                                  onChange={(e) => setEntryField(p, "result_position", e.target.value)}
                                  className="border p-1 w-20"
                                />
                              </td>
                              <td className="p-2 border text-center">{p.points_score ?? "-"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : (
                <section className="rounded-3xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                  <p className="text-slate-600">Load an event and game to start manual result entry.</p>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
